import { computed, nextTick, ref, watch, type ComputedRef, type Ref } from 'vue';
import { defineStore } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import { caseIdFromRoute, ROUTES } from '@/router';
import { useCaseSettings } from '@/stores/caseSettings/store';
import { useSurgicalGuideCase } from '@/stores/surgicalGuide/caseStore';
import { SURGICAL_APPROACHES } from '@/domain/case/surgery';
import { useVersion } from '@/stores/version';
import { useUserStore } from '@/stores/userStore';
import { useOrganisationProfile } from '@/stores/organisationProfile/store';
import { useHipSpineFeatureGate } from '@/stores/featureToggles/useHipSpineFeatureGate';
import { useSurgicalGuideOrders } from '@/stores/surgicalGuideOrders/store';
import { useCtUploadViewModel as useCtUpload } from '@/views/case-settings/upload/ct/useCtUploadViewModel';
import { useXrayUploadViewModel as useXrayUpload } from '@/views/case-settings/upload/xray/useXrayUploadViewModel';
import { allow2d3dPlanning } from '@/util/headMetaTags';
import { setInError } from '@/util/dataState';

type ImageModality = 'xray' | 'ct-scan';

const MODALITIES: { label: string; value: ImageModality }[] = [
    { label: 'CT', value: 'ct-scan' },
    { label: '2D/3D', value: 'xray' },
];

// FL-1690 — kept disabled until product re-scopes this feature; renaming to
// "Mark as unsuitable" was the proposed direction.
const UNASSIGN_SURGEON_FEATURE_FLAG = false;

interface CaseSettingsActionDeps {
    route: ReturnType<typeof useRoute>;
    router: ReturnType<typeof useRouter>;
    store: ReturnType<typeof useCaseSettings>;
    surgicalGuideStore: ReturnType<typeof useSurgicalGuideCase>;
    versionStore: ReturnType<typeof useVersion>;
    orgProfile: ReturnType<typeof useOrganisationProfile>;
    ordersStore: ReturnType<typeof useSurgicalGuideOrders>;
    ctUploadStore: ReturnType<typeof useCtUpload>;
    xrayUploadStore: ReturnType<typeof useXrayUpload>;
}

// ─── Read-only sections ──────────────────────────────────────────────────

/** Case fields and validity derived from the case-settings Model. */
function useCaseFields() {
    const store = useCaseSettings();
    const userStore = useUserStore();

    return {
        surgery: computed(() => store.case.surgery),
        patient: computed(() => store.case.patient),
        specifications: computed(() => store.specifications),
        isCaseReadonly: computed(() => store.isCaseReadonly || userStore.isSurgicalSupportEngineer),
        hasStudy: computed(() => store.hasStudy),
        isSpecificationsEnabled: computed(() => store.isSpecificationsEnabled),
        isSaving: computed(() => store.isSaving),
        isModalityLocked: computed(() => store.case.study?.sourceModality != null),
        canEditCRNumber: computed(() => userStore.canEditCrNumber),
        hasFieldError: (field: string) => store.hasFieldError(field),
        showFieldError: (field: string) => store.state.displayErrors && store.hasFieldError(field),
    };
}

/** Selected imaging modality, kept in sync with the loaded study. */
function useModalitySelection() {
    const store = useCaseSettings();
    const selectedModality = ref<ImageModality>('ct-scan');

    watch(
        () => store.case.study?.sourceModality,
        (sourceModality) => {
            if (sourceModality) selectedModality.value = sourceModality;
        },
        { immediate: true },
    );

    return { selectedModality, modalities: MODALITIES };
}

/** Which feature cards/sections are visible. */
function useCaseSettingsCards(
    isNewCase: ComputedRef<boolean>,
    selectedModality: Ref<ImageModality>,
) {
    const orgProfile = useOrganisationProfile();
    const { enabled: hipSpineFeatureEnabled } = useHipSpineFeatureGate();

    return {
        allow2d3dPlanningEnabled: allow2d3dPlanning(),
        showSurgicalGuideCard: computed(() => orgProfile.features.order_surgical_guide),
        showHipSpineCard: computed(
            () =>
                !isNewCase.value &&
                selectedModality.value === 'ct-scan' &&
                hipSpineFeatureEnabled.value,
        ),
        showUnassignSurgeon: computed(() => UNASSIGN_SURGEON_FEATURE_FLAG),
    };
}

/** Surgical-guide form data and order banner for the case-settings page. */
function useCaseSurgicalGuideSection() {
    const store = useCaseSettings();
    const surgicalGuideStore = useSurgicalGuideCase();
    const ordersStore = useSurgicalGuideOrders();

    return {
        surgicalGuide: surgicalGuideStore.current,
        surgicalApproachOptions: computed(() => SURGICAL_APPROACHES),
        checkSurgicalGuideFieldError: (field: string) =>
            store.state.displayErrors && surgicalGuideStore.hasError(field),
        showOrderedBanner: computed(
            () =>
                surgicalGuideStore.isComplete &&
                !surgicalGuideStore.hasUnsavedChanges &&
                ordersStore.hasOrders,
        ),
    };
}

// ─── Orchestration actions ───────────────────────────────────────────────

async function loadCaseSettings(
    deps: CaseSettingsActionDeps,
    isNewCase: boolean,
    selectedModality: Ref<ImageModality>,
): Promise<void> {
    const { store, surgicalGuideStore, versionStore } = deps;
    const { orgProfile, ordersStore, ctUploadStore, xrayUploadStore, route } = deps;

    store.$reset();
    surgicalGuideStore.$reset();
    ctUploadStore.$reset();
    xrayUploadStore.$reset();
    selectedModality.value = 'ct-scan';

    await versionStore.load();

    if (isNewCase) {
        await store.loadNewCase();
        if (orgProfile.features.order_surgical_guide) {
            const surgeon = store.case.specifications.surgeon;
            const surgeonId = surgeon.selected ?? surgeon.options[0]?.id ?? null;
            if (surgeonId !== null) {
                await surgicalGuideStore.loadNewCase(surgeonId, surgeon.options.length === 1);
            }
        }
    } else {
        const caseId = caseIdFromRoute(route);
        await store.loadExistingCase(caseId);
        await ordersStore.load(caseId);
        if (orgProfile.features.order_surgical_guide) {
            await surgicalGuideStore.loadExistingCase(caseId);
        }
    }
}

// Orchestrates the surgeon change across both Models so each stays unaware of
// the other.
async function updateSurgeon(deps: CaseSettingsActionDeps, surgeonId: number): Promise<void> {
    const { store, surgicalGuideStore, orgProfile } = deps;
    await store.onUpdateSurgeon(surgeonId);
    if (orgProfile.features.order_surgical_guide) {
        await surgicalGuideStore.onUpdateSurgeon(surgeonId);
    }
}

// Saves and, for a successful new-case save, routes to the edit URL. Returns
// whether a route change occurred so the View can run a post-route scroll.
async function saveCaseSettings(
    deps: CaseSettingsActionDeps,
    isNewCase: boolean,
    canEditCRNumber: boolean,
): Promise<{ rerouted: boolean }> {
    const { store, surgicalGuideStore, versionStore, orgProfile, ctUploadStore, router } = deps;
    const hasSurgicalGuide = orgProfile.features.order_surgical_guide;

    // Validity is aggregated across both Models — the Save button is one user
    // intent, so we don't half-save when one Model has errors.
    store.state.displayErrors = true;
    if (!(store.isValid && (!hasSurgicalGuide || surgicalGuideStore.isValid))) {
        await nextTick(); // let state watchers run before the error is set
        setInError(store.state, {
            title: 'Please review your input',
            message:
                'Your changes were not saved. Please review the errors marked in red below and try again.',
        });
        return { rerouted: false };
    }

    // Suppress the upload toast before the saved toast appears (shared slot).
    ctUploadStore.state.hasUploaded = false;

    await store.save(isNewCase, versionStore.webComponentVersion);
    if (store.isError) return { rerouted: false };

    if (hasSurgicalGuide && store.case.id !== null) {
        await surgicalGuideStore.save(store.case.id, canEditCRNumber);
    }

    if (isNewCase) {
        const failure = await router.push({
            name: ROUTES.EDIT_CASE,
            params: { id: store.case.id },
        });
        return { rerouted: !failure };
    }
    return { rerouted: false };
}

function useCaseSettingsActions(
    isNewCase: ComputedRef<boolean>,
    selectedModality: Ref<ImageModality>,
    canEditCRNumber: ComputedRef<boolean>,
) {
    const deps: CaseSettingsActionDeps = {
        route: useRoute(),
        router: useRouter(),
        store: useCaseSettings(),
        surgicalGuideStore: useSurgicalGuideCase(),
        versionStore: useVersion(),
        orgProfile: useOrganisationProfile(),
        ordersStore: useSurgicalGuideOrders(),
        ctUploadStore: useCtUpload(),
        xrayUploadStore: useXrayUpload(),
    };

    return {
        load: () => loadCaseSettings(deps, isNewCase.value, selectedModality),
        onUpdateSurgeon: (surgeonId: number) => updateSurgeon(deps, surgeonId),
        save: () => saveCaseSettings(deps, isNewCase.value, canEditCRNumber.value),
    };
}

export const useCaseSettingsViewModel = defineStore('case-settings-view', () => {
    const store = useCaseSettings();
    const surgicalGuideStore = useSurgicalGuideCase();
    const orgProfile = useOrganisationProfile();
    const route = useRoute();

    const isNewCase = computed(() => route.name === ROUTES.NEW_CASE);
    const pageTitle = computed(() => (isNewCase.value ? 'New Formus Case' : 'Case Settings'));

    const fields = useCaseFields();
    const modality = useModalitySelection();
    const cards = useCaseSettingsCards(isNewCase, modality.selectedModality);
    const surgicalGuide = useCaseSurgicalGuideSection();

    // Aggregate dirtiness across both stores. Surgical guide only contributes
    // when the org has the feature.
    const isDirty = computed(
        () =>
            store.isDirty ||
            (orgProfile.features.order_surgical_guide && surgicalGuideStore.hasUnsavedChanges),
    );
    const canSave = computed(
        () => isDirty.value && !store.isSaving && !store.isCaseDeemedUnsuitable,
    );

    const actions = useCaseSettingsActions(
        isNewCase,
        modality.selectedModality,
        fields.canEditCRNumber,
    );

    return {
        isNewCase,
        pageTitle,
        isDirty,
        canSave,
        ...fields,
        ...modality,
        ...cards,
        ...surgicalGuide,
        ...actions,
    };
});
